import { ROLES, type Role, type StaffMember } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { OpenRecord } from '@/components/queue/queue-parts';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { AddStaffDialog } from '@/components/staff/add-staff-dialog';
import {
  RoleChips,
  StaffIdentity,
  StaffStateBadge,
} from '@/components/staff/staff-parts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { formatDate, formatDateTime } from '@/lib/format';
import { adminQuery } from '@/lib/query';
import {
  filterStaff,
  ROLE_LABELS,
  STAFF_STATE_LABEL,
  STAFF_STATES,
  staffState,
} from '@/lib/staff';
import { useCan } from '@/lib/staff-context';

const searchSchema = z.object({
  role: z.enum(ROLES).optional().catch(undefined),
  status: z.enum(STAFF_STATES).optional().catch(undefined),
  q: z.string().max(80).optional(),
});

type StaffSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_app/staff/')({
  validateSearch: searchSchema,
  component: StaffPage,
});

const column = createColumnHelper<StaffMember>();

const COLUMNS = [
  column.accessor('display_name', {
    header: 'Staff member',
    cell: ({ row }) => (
      <OpenRecord
        sheetRef={{ type: 'staff', id: row.original.id }}
        label={`Open ${row.original.display_name}`}
      >
        <StaffIdentity member={row.original} />
      </OpenRecord>
    ),
  }),
  column.accessor((row) => row.roles.join(' '), {
    id: 'roles',
    header: 'Roles',
    cell: ({ row }) => <RoleChips member={row.original} />,
  }),
  column.accessor((row) => STAFF_STATE_LABEL[staffState(row)], {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <StaffStateBadge member={row.original} />,
  }),
  column.accessor('last_sign_in_at', {
    header: 'Last sign-in',
    cell: (info) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDateTime(info.getValue()) || 'Never'}
      </span>
    ),
  }),
  column.accessor('created_at', {
    header: 'Added',
    cell: (info) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDate(info.getValue())}
      </span>
    ),
  }),
];

function StaffPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const staff = useQuery(adminQuery('staff.list', {}));
  const canAdd = useCan('staff.add');
  const sheetActions = useSheetActions();
  const [adding, setAdding] = useState(false);
  const set = (patch: Partial<StaffSearch>) =>
    void navigate({ search: { ...search, ...patch }, replace: true });
  const filtered = Boolean(search.role || search.status || search.q);

  return (
    <>
      <PageHeader
        title="Staff"
        description="Everyone who can sign in to the portal. Only the super admin adds staff and sets roles."
        actions={
          canAdd ? (
            <Button onClick={() => setAdding(true)}>Add staff member</Button>
          ) : null
        }
      />
      <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <div className="col-span-2 flex flex-col gap-1.5 sm:w-64">
          <Input
            aria-label="Search by name or email"
            placeholder="Search by name or email"
            value={search.q ?? ''}
            onChange={(event) => set({ q: event.target.value || undefined })}
          />
        </div>
        <Select
          label="Role"
          className="w-full sm:w-44"
          value={search.role ?? ''}
          onChange={(event) =>
            set({ role: (event.target.value || undefined) as Role | undefined })
          }
        >
          <option value="">Any</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </Select>
        <Select
          label="Status"
          className="w-full sm:w-44"
          value={search.status ?? ''}
          onChange={(event) =>
            set({
              status: (event.target.value || undefined) as
                | StaffSearch['status']
                | undefined,
            })
          }
        >
          <option value="">Any</option>
          {STAFF_STATES.map((state) => (
            <option key={state} value={state}>
              {STAFF_STATE_LABEL[state]}
            </option>
          ))}
        </Select>
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() =>
              set({ role: undefined, status: undefined, q: undefined })
            }
          >
            Clear filters
          </Button>
        ) : null}
      </div>
      <QueryState
        query={staff}
        label="staff"
        skeleton={<SkeletonTable columns={5} />}
      >
        {(rows) => {
          const shown = filterStaff(rows, search);
          return shown.length === 0 ? (
            <EmptyState
              variant={filtered ? 'filtered' : 'clear'}
              title={
                filtered ? 'No staff match these filters.' : 'No staff yet.'
              }
            />
          ) : (
            <DataTable
              id="staff"
              columns={COLUMNS}
              data={shown}
              pageSize={100}
              rowLabel={(row) => `Open ${row.display_name}`}
              onRowClick={(row, rowEl) =>
                sheetActions.open(
                  { type: 'staff', id: row.id },
                  { fromRowEl: rowEl.querySelector('button') ?? rowEl },
                )
              }
            />
          );
        }}
      </QueryState>
      {canAdd ? (
        <AddStaffDialog open={adding} onOpenChange={setAdding} />
      ) : null}
    </>
  );
}
